import * as React from "react";
import * as moment from "moment";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { MDBBtn, MDBCol, MDBRow } from "mdbreact";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import { Pager } from "../utils/Pager";
import { Alert, Button, Classes, FormGroup, H5, InputGroup, Popover, Tab, Tabs } from "@blueprintjs/core";
import { ManifestacoesStore } from "../../stores/manifestacoes/ManifestacoesStore";
import { IEmpresa, newEmpresa } from "../../model";
import { AppStore } from "../../stores/AppStore";
import { formatCnpj } from "../../utils";
import { toast } from "react-toastify";
import { JsonEditor } from "json-edit-react";
import LoadingModal from "../loading/LoadingModal";

@observer
export default class NsuManifestascaoPage extends React.Component<{}, {}> {

  obs = observable({
    tarefas_page: 1 as number,
    erros_relatorio: [] as any,
    open_erros_relatorio: false as boolean,
    selectedEmpresa: newEmpresa() as IEmpresa,
    modelo: "" as string,
    status: "" as string,
    selectedTab: "nsuManifestacao" as string,
    nsu_manifestacao: "" as string,
    tarefa_original: null as any,
    tarefa_editada: {} as any,
    loading_editar: false as boolean,
    open_editar: false as boolean
  })

  /** 
  * Lista de campos restritos que não devem ser editados pelo usuário. 
  * Define os campos técnicos, identificadores e informações internas que devem 
  * ser protegidos durante operações de edição ou montagem de alterações no front. 
  * Esses campos normalmente são gerados pelo banco de dados ou utilizados pelo 
  * sistema para controle interno, por isso não devem ser enviados como campos 
  * editáveis em atualizações. 
  * @readonly 
  * @type {string[]} 
  */

  readonly restrictData = [
    "_id", "_insertedTS", "_modifiedTS", "conta_id", "manifestacao_id", "cnpj", "type", "nome_fantasia", "inserted_at"
  ];

  /**
  * Executa ações iniciais após a montagem do componente.
  *
  * Define o título da página como `"NSU Manifestações"` através da `AppStore`
  * e inicializa a aba selecionada chamando `handleChangeTab` com o valor atual
  * de `this.obs.selectedTab`.
  *
  * Essa inicialização garante que a tela seja carregada com o título correto
  * e com os filtros/listagens da aba atual devidamente redefinidos.
  *
  * @returns {void} Não retorna valor.
  */

  componentDidMount() {
    AppStore.setTitle("NSU Manifestações");
    this.handleChangeTab(this.obs.selectedTab);
  }

  /**
  * Altera a aba selecionada na tela e redefine os filtros da listagem.
  *
  * Atualiza o estado observável `selectedTab` com a aba informada e limpa os
  * campos utilizados nos filtros de NSU e modelo.
  *
  * Também limpa a listagem administrativa de manifestações/NSU armazenada na
  * store e redefine o total de registros para zero, evitando que dados da aba
  * anterior permaneçam visíveis após a troca.
  *
  * @param {string} tabId Identificador da aba que será selecionada.
  * @returns {void} Não retorna valor.
  */

  handleChangeTab(tabId: string) {
    this.obs.selectedTab = tabId;

    this.obs.nsu_manifestacao = "";
    this.obs.modelo = "";
    ManifestacoesStore.obs.nsu_manifestacoes_admin = [];
    ManifestacoesStore.obs.length_nsu_manifestacoes_admin = 0;
  }

  /**
  * Inicializa o componente com as propriedades recebidas.
  *
  * Chama o construtor da classe pai através de `super(props)`, garantindo que
  * as propriedades do componente sejam repassadas corretamente para a estrutura
  * base do React.
  *
  * @param {any} props Propriedades recebidas pelo componente.
  * @returns {void} Não retorna valor.
  */

  constructor(props: any) {
    super(props);
  }

  /**
  * Executa a busca das tarefas de manifestações para download.
  *
  * Chama a store responsável por consultar as tarefas de manifestações,
  * utilizando a página informada, limite fixo de registros por página e os
  * filtros selecionados na tela.
  *
  * Os filtros utilizados são o CNPJ da empresa selecionada, o modelo da
  * manifestação e o status atual definido no estado observável.
  *
  * @async
  * @param {number} page Página que será consultada na listagem.
  * @returns {Promise<void>} Não retorna valor diretamente.
  */

  handleBuscaTarefas = async (page: number) => {
    ManifestacoesStore.handleConsultaTasksManifestacoesDownload(page, 15, this.obs.selectedEmpresa.cnpj, this.obs.modelo, this.obs.status);
  };

  /**
  * Atualiza o filtro de modelo com base na opção selecionada na tela.
  *
  * Captura o valor do evento disparado pelo campo de seleção/input e atribui
  * esse valor ao estado observável `modelo`, utilizado posteriormente nos filtros
  * da listagem de manifestações.
  *
  * @param {any} e Evento disparado pelo componente de filtro, contendo o valor selecionado em `e.target.value`.
  * @returns {void} Não retorna valor.
  */

  handleFilterModeloChange = (e: any) => {
    var string = e.target.value;
    this.obs.modelo = string;
  };

  /**
  * Executa a busca de manifestações com base nos filtros informados na tela.
  *
  * Antes de realizar a busca, valida se o CNP foi preenchido.
  *
  * Após as validações, monta os parâmetros da consulta, redefine a paginação
  * para a primeira página, ativa o loading da listagem e chama a store
  * responsável por carregar as manifestações.
  *
  * @async
  * @returns {Promise<void>} Não retorna valor diretamente.
  */

  handleBuscaClick = async () => {
    this.obs.tarefas_page = 1;

    if (!this.obs.nsu_manifestacao) {
      return toast.warning("É necessário informar o CNPJ de uma empresa!");
    }

    await ManifestacoesStore.handleConsultaNsu(this.obs.tarefas_page, 15, this.obs.nsu_manifestacao, this.obs.modelo);

    if (ManifestacoesStore.obs.nsu_manifestacoes_admin.length === 0) {
      toast.warning("Nenhum registro encontrado para o CNPJ informado.");
    }
  };

  /**
  * Executa a busca de manifestações de NFSe.
  *
  * Antes de realizar a busca, valida se o CNPJ foi informado.
  *
  * Após as validações, monta os parâmetros da consulta, redefine a paginação
  * para a primeira página, ativa o loading da listagem e chama a store
  * responsável por carregar as manifestações.
  *
  * @async
  * @returns {Promise<void>} Não retorna valor diretamente.
  */

  handleBuscaClickNfse = async () => {
    this.obs.tarefas_page = 1;

    if (!this.obs.nsu_manifestacao) {
      return toast.warning("É necessário informar o CNPJ de uma empresa!");
    }

    await ManifestacoesStore.handleConsultaNsuNfseAdmin(this.obs.tarefas_page, 15, this.obs.nsu_manifestacao, "");

    if (ManifestacoesStore.obs.nsu_manifestacoes_admin.length === 0) {
      toast.warning("Nenhum registro encontrado para o CNPJ informado.");
    }
  };

  /**
  * Abre o modal de edição para uma tarefa selecionada.
  *
  * Armazena a tarefa original, cria uma cópia independente dos dados para edição
  * e altera o estado responsável por exibir o modal/painel de edição.
  *
  * A cópia da tarefa é importante para evitar alterações diretas no objeto
  * original antes que o usuário confirme o salvamento.
  *
  * @param {any} row - Objeto da tarefa selecionada na listagem.
  * @returns {void}
  */

  handleEdit = (row: any) => {
    this.obs.tarefa_original = row;
    this.obs.tarefa_editada = this.cloneJson(row);
    this.obs.open_editar = true;
  };

  /**
  * Fecha o modal de edição e limpa os dados temporários da tarefa editada.
  *
  * Esse método é utilizado quando o usuário cancela ou encerra a edição.
  * Ele redefine o estado relacionado à edição, removendo a tarefa original
  * e limpando o objeto usado para armazenar os dados editados.
  *
  * @returns {void}
  */

  handleCloseEdit = () => {
    this.obs.open_editar = false;
    this.obs.tarefa_original = null;
    this.obs.tarefa_editada = {};
  };

  /**
  * Verifica se o primeiro nível de um caminho corresponde a um campo sensível/restrito.
  * O método compara o primeiro item do `path` com a lista `restrictData`.
  * Caso o campo esteja nessa lista, ele deve ser tratado como protegido, 
  * impedindo ou limitando sua edição manual no JSON. 
  * @param {any[]} path - Caminho do campo dentro do objeto JSON. 
  * @returns {boolean} Retorna `true` se o campo for sensível/restrito. 
  */

  isCampoSensivel = (path: any[] = []) => {
    return this.restrictData.includes(String(path?.[0]));
  };

  /** 
  * Verifica se um valor é um objeto simples. 
  * Retorna `true` apenas quando o valor é diferente de `null`, 
  * possui tipo `"object"` e não é um array. 
  * @param {any} value - Valor que será validado. 
  * @returns {boolean} Retorna `true` se o valor for um objeto simples. 
  */

  isObject = (value: any) => {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  };

  /**
  * Gera um patch com as diferenças entre o objeto original e o objeto editado.
  *
  * O método percorre recursivamente os dois objetos e identifica quais campos
  * foram alterados, adicionados ou removidos. As alterações são organizadas em
  * dois grupos:
  *
  * - `$set`: campos que devem ser criados ou atualizados;
  * - `$unset`: campos que devem ser removidos.
  *
  * Campos presentes em `restrictData` são ignorados quando estão no primeiro
  * nível do objeto, impedindo alterações em dados sensíveis ou protegidos.
  *
  * Arrays são comparados como um todo. Caso exista diferença entre o array
  * original e o array editado, o array inteiro é enviado em `$set`.
  *
  * @param {any} original - Objeto original antes da edição.
  * @param {any} editado - Objeto contendo os dados editados.
  * @param {Array<string | number>} path - Caminho atual dentro do objeto durante a recursão.
  * @param {{ $set: Record<string, any>, $unset: Record<string, 1> }} patch - Objeto acumulador das alterações.
  * @returns {{ $set: Record<string, any>, $unset: Record<string, 1> }} Patch contendo os campos alterados e removidos.
  */

  getPatch = (
    original: any,
    editado: any,
    path: Array<string | number> = [],
    patch = {
      $set: {} as Record<string, any>,
      $unset: {} as Record<string, 1>
    }
  ) => {
    const originalObj = this.isObject(original);
    const editadoObj = this.isObject(editado);

    if (Array.isArray(original) || Array.isArray(editado)) {
      if (!this.isEqualJson(original, editado)) {
        const key = this.pathToKey(path);

        if (key) {
          patch.$set[key] = editado;
        }
      }

      return patch;
    }

    if (!originalObj || !editadoObj) {
      if (!this.isEqualJson(original, editado)) {
        const key = this.pathToKey(path);

        if (key) {
          patch.$set[key] = editado;
        }
      }

      return patch;
    }

    const keys = new Set([
      ...Object.keys(original || {}),
      ...Object.keys(editado || {})
    ]);

    keys.forEach((key) => {

      if (path.length === 0 && this.restrictData.includes(key)) {
        return;
      }

      const nextPath = [...path, key];

      if (!(key in editado)) {
        patch.$unset[this.pathToKey(nextPath)] = 1;
        return;
      }

      if (!(key in original)) {
        patch.$set[this.pathToKey(nextPath)] = editado[key];
        return;
      }

      this.getPatch(original[key], editado[key], nextPath, patch);
    });

    return patch;
  };

  /**
  * Compara dois valores JSON para verificar se são equivalentes.
  * A comparação é feita convertendo os valores para string JSON.
  * Essa abordagem funciona bem para estruturas simples, como objetos, 
  * arrays, strings, números, booleanos e valores nulos. 
  * @param {any} a - Primeiro valor a ser comparado. 
  * @param {any} b - Segundo valor a ser comparado. 
  * @returns {boolean} Retorna `true` se os valores forem equivalentes em JSON. 
  */

  isEqualJson = (a: any, b: any) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  /** 
  * Converte um caminho de propriedades em uma chave no formato string. 
  * Recebe um array representando o caminho de um campo dentro do JSON 
  * e transforma esse caminho em uma string separada por pontos. 
  * Exemplo: * `["empresa", "endereco", "cidade"]` vira `"empresa.endereco.cidade"`. 
  * @param {Array<string | number>} path - Caminho do campo no objeto JSON. 
  * @returns {string} Chave formatada com os níveis separados por ponto.
  */

  pathToKey = (path: Array<string | number>) => {
    return path.map(String).join(".");
  };

  /**
  * Salva as alterações realizadas na edição de uma tarefa/bot.
  *
  * Valida se existe uma tarefa original identificável, calcula as diferenças
  * entre o objeto original e o objeto editado, monta um patch com os campos
  * alterados e envia essas alterações para a API.
  *
  * Caso nenhuma alteração tenha sido feita, a operação é interrompida e uma
  * mensagem informativa é exibida ao usuário.
  *
  * Após salvar com sucesso, fecha o modal de edição, limpa os dados temporários
  * da edição e atualiza a listagem chamando `handleBuscaClick`.
  *
  * @async
  * @returns {Promise<void>} Não retorna valor diretamente.
  */

  handleSaveEdit = async (row) => {
    if (!this.obs.tarefa_original?._id) {
      toast.error("Não foi possível identificar a tarefa para salvar.");
      return;
    }

    try {
      this.obs.loading_editar = true;

      const patch = this.getPatch(
        this.obs.tarefa_original,
        this.obs.tarefa_editada
      );

      const hasSet = Object.keys(patch.$set).length > 0;
      const hasUnset = Object.keys(patch.$unset).length > 0;

      if (!hasSet && !hasUnset) {
        toast.info("Nenhuma alteração foi feita.");
        return;
      }

      const params = {
        id: this.obs.tarefa_original._id,
        type: row.type,
        campos_alterados: patch
      };

      await ManifestacoesStore.handleUpdateNsuManifestacaoAdmin(params);

      this.obs.open_editar = false;
      this.obs.tarefa_original = null;
      this.obs.tarefa_editada = {};

      if (this.obs.selectedTab === "nsuManifestacaoNfse") {
        await this.handleBuscaClickNfse();
      } else {
        await this.handleBuscaClick();
      }

    } catch (error) {
      console.error(error);
      toast.error("Não foi possível salvar a edição do JSON.");
    } finally {
      this.obs.loading_editar = false;
    }
  };

  /**
  * Cria uma cópia de um objeto JSON.
  *
  * Converte o valor recebido para string JSON e depois o reconverte para objeto,
  * gerando uma nova referência em memória. Isso evita que alterações feitas na
  * cópia afetem diretamente o objeto original.
  *
  * Caso o valor recebido seja nulo, indefinido ou falso, utiliza um objeto vazio
  * como valor padrão.
  *
  * @param {any} value - Valor que será clonado.
  * @returns {any} Cópia do valor informado, ou um objeto vazio.
  */

  cloneJson = (value: any) => {
    return JSON.parse(JSON.stringify(value || {}));
  };

  /**
  * Função responsável por excluir um registro de manifestação/NSU e atualizar a listagem exibida.
  *
  * Define a mensagem de carregamento apresentada ao usuário, executa a exclusão da manifestação
  * através da store `ManifestacoesStore` e, ao final do processo, recarrega a listagem de acordo
  * com a aba selecionada no frontend.
  *
  * ## Parâmetros
  *
  * - `row` (`any`): Objeto da linha selecionada na tabela, contendo os dados do registro de manifestação/NSU que será excluído.
  *
  * ## Comportamento
  *
  * **Exclusão da manifestação**:
  *
  * - A função chama `ManifestacoesStore.handleDeleteNsuManifestacao(row)`.
  * - Essa chamada é responsável por executar a lógica de exclusão do registro, normalmente realizando a requisição para o backend.
  * - O uso de `await` garante que a listagem só será atualizada após a finalização da exclusão.
  *
  * **Atualização da listagem**:
  *
  * - Após a exclusão, a função verifica qual aba está selecionada através de `this.obs.selectedTab`.
  * - Caso a aba selecionada seja `"nsuManifestacaoNfse"`, a função recarrega a listagem de NFS-e chamando:
  *
  *   ```ts
  *   this.handleBuscaClickNfse()
  *   ```
  *
  * - Para as demais abas, a função recarrega a listagem padrão de manifestações chamando:
  *
  *   ```ts
  *   this.handleBuscaClick()
  *   ```
  * ## Observações
  *
  * - A função é assíncrona e deve ser chamada com `await` quando for necessário aguardar a conclusão da exclusão.
  * - A atualização da listagem depende da aba atualmente selecionada.
  * - O objeto `row` deve conter as informações necessárias para que a store consiga identificar e excluir o registro corretamente.
  */

  handleDeleteNsuManifestacao = async (row: any) => {
    ManifestacoesStore.obs.title_loading = "Excluindo manifestação!";
    await ManifestacoesStore.handleDeleteNsuManifestacao(row);

    if (this.obs.selectedTab === "nsuManifestacaoNfse") {
      await this.handleBuscaClickNfse();
    } else {
      await this.handleBuscaClick();
    }
  };

  render() {

    let niveis_acesso = AppStore.obs1.usuario
      ? AppStore.obs1.usuario.niveis_acesso
      : [""];

    function stringFormatter(cell, row) {
      if (!cell) return "";
      return (cell).toUpperCase();
    };

    function dateFormatter(cell, row) {
      return moment(cell).add(3, "hour").format("DD/MM/YYYY") + " às " + moment(cell).add(3, "hour").format("HH:mm");
    };

    function getRazaoSocial(cell, row) {

      return `${row.razao_social} - (${formatCnpj(row.cnpj)})`

    }

    return (
      <>

        <LoadingModal open={ManifestacoesStore.obs.loading} onClose={() => ManifestacoesStore.obs.loading = false} title={ManifestacoesStore.obs.title_loading} />

        <Tabs
          id="tabs"
          className="tabsCalculos"
          selectedTabId={this.obs.selectedTab}
          onChange={(tabId) => this.handleChangeTab(tabId as string)}
        >

          <Tab id="nsuManifestacao" title="NFe, CTe, NFCe" panel={
            <>
              <MDBRow>
                <MDBCol sm="12" md="6" lg="3" xl="3">
                  <FormGroup label="CNPJ">
                    <InputGroup
                      placeholder="Digite o CNPJ"
                      value={this.obs.nsu_manifestacao}
                      onChange={(e) => (this.obs.nsu_manifestacao = e.target.value)}
                      fill
                    />
                  </FormGroup>
                </MDBCol>
                <MDBCol xl="2" lg="2" md="2" sm="2" xs="12">
                  <FormGroup
                    label="Modelo"
                    className="nz-horizontal-field selectFullWidth"
                  >
                    <select
                      onChange={this.handleFilterModeloChange}
                      className="bp3-button bp3-align-left"
                    >
                      <option value="">Todos</option>
                      <option value="nfe">NF-e</option>
                      <option value="nfce">NFC-e</option>
                      <option value="cte">CT-e</option>
                    </select>
                  </FormGroup>
                </MDBCol>
                <MDBCol md="2">
                  <MDBBtn
                    className="btnDefault btnBuscarNfe mt25"
                    onClick={this.handleBuscaClick}
                    disabled={niveis_acesso.indexOf("h1") == -1}
                  >
                    Buscar
                  </MDBBtn>
                </MDBCol>
              </MDBRow>
              
              <div data-tut="table">
                <BootstrapTable className="TableDefault" ref="table" data={ManifestacoesStore.obs.nsu_manifestacoes_admin} data-tut="table">
                  <TableHeaderColumn dataField="_id" isKey={true} hidden width="1%"> # </TableHeaderColumn>
                  <TableHeaderColumn dataField='_id' width='10%'>ID</TableHeaderColumn>
                  <TableHeaderColumn dataFormat={getRazaoSocial} dataField="cnpj" width="20%">Empresa</TableHeaderColumn>
                  <TableHeaderColumn dataFormat={dateFormatter} dataField="_insertedTS" width="15%">Data</TableHeaderColumn>
                  <TableHeaderColumn dataFormat={dateFormatter} dataField="_modifiedTS" width="15%">Data Atualização</TableHeaderColumn>
                  <TableHeaderColumn dataFormat={stringFormatter} dataField="type" width="5%">Modelo</TableHeaderColumn>
                  <TableHeaderColumn dataField="nsu" width="10%">NSU</TableHeaderColumn>
                  <TableHeaderColumn
                    dataField="editar"
                    width="5%"
                    dataFormat={(cell, row) => (
                      <MDBBtn
                        color="default"
                        rounded
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          this.handleEdit(row);
                        }}
                      >
                        <img src="/images/edit.png" width="25px" alt="Editar" />
                      </MDBBtn>
                    )}
                    thStyle={{ textAlign: 'center' }}
                    tdStyle={{ textAlign: 'center' }}
                  >
                    Editar
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    dataField="excluir"
                    width="5%"
                    dataFormat={(cell, row) => (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Popover popoverClassName={Classes.POPOVER_CONTENT_SIZING} interactionKind="click">
                          <MDBBtn color="default" rounded size="sm">
                            <img src="/images/bin.png" width="25px" alt="Excluir" />
                          </MDBBtn>
                          <div>
                            <H5>Confirmar exclusão</H5>
                            <p style={{ fontSize: 12 }}>
                              Tem certeza de que deseja excluir esse item? Você não será capaz de recuperá-lo.
                            </p>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 15 }}>
                              <Button className={Classes.POPOVER_DISMISS} style={{ marginRight: 10 }}>
                                Cancelar
                              </Button>
                              <Button
                                intent="danger"
                                className={Classes.POPOVER_DISMISS}
                                onClick={() => this.handleDeleteNsuManifestacao(row)}
                              >
                                Excluir
                              </Button>
                            </div>
                          </div>
                        </Popover>
                      </div>
                    )}
                    thStyle={{ textAlign: 'center' }}
                    tdStyle={{ textAlign: 'center' }}
                  >
                    Excluir
                  </TableHeaderColumn>
                </BootstrapTable>
              </div>

              <div style={{ marginTop: 5, float: "right", marginBottom: 120 }}>
                <Pager
                  currentPage={this.obs.tarefas_page}
                  totalCount={ManifestacoesStore.obs.length_nsu_manifestacoes_admin}
                  pageSize={15}
                  onPageChange={page => this.handleBuscaTarefas(this.obs.tarefas_page = page)}
                />
              </div>

              <Alert
                confirmButtonText={this.obs.loading_editar ? "Salvando..." : "Salvar"}
                cancelButtonText="Cancelar"
                isOpen={this.obs.open_editar}
                onConfirm={() => this.handleSaveEdit(this.obs.tarefa_original)}
                onCancel={() => this.handleCloseEdit()}
                onClose={() => this.handleCloseEdit()}
                canOutsideClickCancel={false}
                canEscapeKeyCancel={true}
                intent="primary"
                className="AlertUser alert-json-edit"
              >
                <h2 className="titleSEFAZ">Editar JSON da Manifestação</h2>

                <div
                  style={{
                    maxHeight: "70vh",
                    overflow: "auto",
                    textAlign: "left",
                    padding: 10
                  }}
                >
                  <JsonEditor
                    data={this.obs.tarefa_editada}
                    setData={(data: any) => {
                      this.obs.tarefa_editada = data;
                    }}
                    rootName="tarefa"
                    collapse={2}
                    indent={3}
                    enableClipboard={true}
                    restrictDrag={true}
                    restrictEdit={(node: any) => this.isCampoSensivel(node.path)}
                    restrictAdd={true}
                    restrictDelete={true}
                    restrictTypeSelection={true}
                  />
                </div>
              </Alert>

            </>

          } />

          <Tab id="nsuManifestacaoNfse" title="NFSe" panel={
            <>
              <MDBRow>
                <MDBCol sm="12" md="6" lg="3" xl="3">
                  <FormGroup label="CNPJ">
                    <InputGroup
                      placeholder="Digite o CNPJ"
                      value={this.obs.nsu_manifestacao}
                      onChange={(e) => (this.obs.nsu_manifestacao = e.target.value)}
                      fill
                    />
                  </FormGroup>
                </MDBCol>
                <MDBCol md="2">
                  <MDBBtn
                    className="btnDefault btnBuscarNfe mt25"
                    onClick={this.handleBuscaClickNfse}
                    disabled={niveis_acesso.indexOf("h1") == -1}
                  >
                    Buscar
                  </MDBBtn>
                </MDBCol>
              </MDBRow>
              
              <div data-tut="table">
                <BootstrapTable className="TableDefault" ref="table" data={ManifestacoesStore.obs.nsu_manifestacoes_admin}>
                  <TableHeaderColumn dataField="_id" isKey={true} hidden width="1%">#</TableHeaderColumn>
                  <TableHeaderColumn dataField="_id" width="10%">ID</TableHeaderColumn>
                  <TableHeaderColumn dataFormat={getRazaoSocial} dataField="cnpj" width="25%">Empresa</TableHeaderColumn>
                  <TableHeaderColumn dataFormat={dateFormatter} dataField="inserted_at" width="15%">Data</TableHeaderColumn>
                  <TableHeaderColumn dataField="nsu" width="10%">NSU</TableHeaderColumn>
                  <TableHeaderColumn
                    dataField="editar"
                    width="5%"
                    dataFormat={(cell, row) => (
                      <MDBBtn
                        color="default"
                        rounded
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          this.handleEdit(row);
                        }}
                      >
                        <img src="/images/edit.png" width="25px" alt="Editar" />
                      </MDBBtn>
                    )}
                    thStyle={{ textAlign: 'center' }}
                    tdStyle={{ textAlign: 'center' }}
                  >
                    Editar
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    dataField="excluir"
                    width="5%"
                    dataFormat={(cell, row) => (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Popover popoverClassName={Classes.POPOVER_CONTENT_SIZING} interactionKind="click">
                          <MDBBtn color="default" rounded size="sm">
                            <img src="/images/bin.png" width="25px" alt="Excluir" />
                          </MDBBtn>
                          <div>
                            <H5>Confirmar exclusão</H5>
                            <p style={{ fontSize: 12 }}>
                              Tem certeza de que deseja excluir esse item? Você não será capaz de recuperá-lo.
                            </p>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 15 }}>
                              <Button className={Classes.POPOVER_DISMISS} style={{ marginRight: 10 }}>
                                Cancelar
                              </Button>
                              <Button
                                intent="danger"
                                className={Classes.POPOVER_DISMISS}
                                onClick={() => this.handleDeleteNsuManifestacao(row)}
                              >
                                Excluir
                              </Button>
                            </div>
                          </div>
                        </Popover>
                      </div>
                    )}
                    thStyle={{ textAlign: 'center' }}
                    tdStyle={{ textAlign: 'center' }}
                  >
                    Excluir
                  </TableHeaderColumn>
                </BootstrapTable>
              </div>

              <div style={{ marginTop: 5, float: "right", marginBottom: 120 }}>
                <Pager
                  currentPage={this.obs.tarefas_page}
                  totalCount={ManifestacoesStore.obs.length_nsu_manifestacoes_admin}
                  pageSize={15}
                  onPageChange={page => this.handleBuscaTarefas(this.obs.tarefas_page = page)}
                />
              </div>
            </>
          } />
        </Tabs>
      </>
    )
  }
}

